import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { apiConfiguration } from '../../../../configurations/api-configuration';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { NotificationService } from '../../../application/services/NotificationService';
import { MessagingApiService } from '../../services/messaging-api/MessagingApiService';
import {
  MailingAudienceMode,
  MailingAudiencePreview,
  MailingCampaign,
  MailingImageMode,
  MailingStatus,
  MailingWorkerRunResult,
  MessagingContext,
  MessagingTeam,
  SaveMailingCampaignData,
  TelegramAccount,
} from '../../types/Messaging';

@Component({
  selector: 'app-mailings-page',
  standalone: false,
  templateUrl: './mailings-page.component.html',
  styleUrl: './mailings-page.component.sass',
})
export class MailingsPageComponent implements OnInit {
  public mode: 'list' | 'create' | 'edit' = 'list';
  public mailingId: string | null = null;
  public context: MessagingContext = {
    users: [],
    teams: [],
    telegramAccounts: [],
    telegramBots: [],
    telegramParsers: [],
  };
  public campaigns: MailingCampaign[] = [];
  public preview: MailingAudiencePreview | null = null;
  public formData: SaveMailingCampaignData = this.createEmptyForm();
  public teamsMenuOpen = false;
  public loading = false;
  public saving = false;
  public previewing = false;
  public dispatching = false;
  public uploadingImage = false;
  public uploadedImageName: string | null = null;
  public lastDispatchResult: MailingWorkerRunResult | null = null;
  public editingCampaign: MailingCampaign | null = null;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public readonly imageModes: Array<{ value: MailingImageMode; label: string }> = [
    { value: 'none', label: 'Без картинки' },
    { value: 'attachment', label: 'Картинка вложением' },
    { value: 'caption', label: 'Текст подписью к картинке' },
  ];

  public readonly audienceModes: Array<{ value: MailingAudienceMode; label: string }> = [
    { value: 'all_responsible_clients', label: 'Все клиенты ответственного' },
    { value: 'team_subscribers', label: 'По подписке на команды' },
  ];

  public constructor(
    private messagingApi: MessagingApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.resolvePageMode();
    await this.loadData();
    if (this.mode === 'edit') {
      await this.loadEditingCampaign();
    }
  }

  public get isListMode(): boolean {
    return this.mode === 'list';
  }

  public get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  public get pageTitle(): string {
    if (this.mode === 'create') {
      return 'Новая рассылка';
    }
    if (this.editingCampaign) {
      return `Редактирование ${this.editingCampaign.code}`;
    }

    return 'Рассылки';
  }

  public get pageDescription(): string {
    if (this.mode === 'list') {
      return 'Telegram-кампании, очередь отправки и история рассылок.';
    }

    return 'Настройка аудитории, текста, вложения и расписания отправки.';
  }

  public get formTitle(): string {
    return this.isEditMode && this.editingCampaign ? `Рассылка ${this.editingCampaign.code}` : 'Параметры рассылки';
  }

  public get submitButtonLabel(): string {
    if (this.saving) {
      return this.isEditMode ? 'Сохраняем...' : 'Создаем...';
    }

    return this.isEditMode ? 'Сохранить изменения' : 'Создать рассылку';
  }

  public get canDeleteEditingCampaign(): boolean {
    return Boolean(this.editingCampaign && this.canModifyCampaign(this.editingCampaign));
  }

  public get filteredAccounts(): TelegramAccount[] {
    return this.context.telegramAccounts.filter((account) => (
      account.userId === this.formData.senderUserId
      && account.authStatus === 'authorized'
      && account.sessionStringConfigured
    ));
  }

  public get selectedTeamsText(): string {
    const teams = this.context.teams.filter((team) => this.formData.teamIds.includes(team.id));
    if (!teams.length) {
      return 'Команды не выбраны';
    }
    if (teams.length === 1) {
      return teams[0].name;
    }

    const names = teams.slice(0, 2).map((team) => team.name).join(', ');
    return `${teams.length} команды: ${names}${teams.length > 2 ? '...' : ''}`;
  }

  public get sortedTeams(): MessagingTeam[] {
    return [
      ...this.context.teams,
    ].sort((left, right) => left.championship.localeCompare(right.championship, 'ru') || left.name.localeCompare(right.name, 'ru'));
  }

  public get plannedCampaigns(): number {
    return this.campaigns.filter((campaign) => campaign.status === 'scheduled').length;
  }

  public resolveImageUrl(imageUrl: string | null): string | null {
    if (!imageUrl) {
      return null;
    }
    if (/^(https?:|data:|blob:)/.test(imageUrl)) {
      return imageUrl;
    }

    const apiOrigin = apiConfiguration.baseApiUrl.replace(/\/api\/?$/, '');
    return `${apiOrigin}${imageUrl}`;
  }

  public onSenderChange(): void {
    this.formData.telegramAccountId = this.filteredAccounts[0]?.id ?? '';
    this.preview = null;
  }

  public toggleTeamsMenu(): void {
    this.teamsMenuOpen = !this.teamsMenuOpen;
  }

  public toggleTeam(teamId: string): void {
    this.preview = null;
    if (this.formData.teamIds.includes(teamId)) {
      this.formData.teamIds = this.formData.teamIds.filter((selectedTeamId) => selectedTeamId !== teamId);
      return;
    }

    this.formData.teamIds = [
      ...this.formData.teamIds,
      teamId,
    ];
  }

  public isTeamSelected(teamId: string): boolean {
    return this.formData.teamIds.includes(teamId);
  }

  public onImageModeChange(): void {
    if (this.formData.imageMode === 'none') {
      this.removeImage();
      return;
    }

    this.preview = null;
  }

  public async previewCampaign(): Promise<void> {
    this.errorMessage = this.validateForm(false);
    if (this.errorMessage) {
      return;
    }

    this.previewing = true;
    this.preview = null;
    try {
      const response = await this.messagingApi.previewMailing(this.preparePayload());
      this.preview = this.unwrapResponse(response);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.previewing = false;
      this.changeDetector.detectChanges();
    }
  }

  public async createCampaign(): Promise<void> {
    this.errorMessage = this.validateForm(true);
    if (this.errorMessage) {
      return;
    }
    if (this.isEditMode) {
      await this.updateCampaign();
      return;
    }

    this.saving = true;
    this.successMessage = null;
    try {
      const response = await this.messagingApi.createMailing(this.preparePayload());
      const campaign = this.unwrapResponse(response);
      if (!campaign) {
        return;
      }
      this.successMessage = 'Рассылка создана и очередь получателей сформирована.';
      this.notificationService.notify('mailing', 'Рассылка создана', `${campaign.title}: ${campaign.recipientCount.toString()} получателей`);
      await this.router.navigate(['/dashboard/mailings']);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteCampaign(): Promise<void> {
    if (!this.editingCampaign || !this.canDeleteEditingCampaign) {
      return;
    }
    if (!window.confirm(`Удалить рассылку ${this.editingCampaign.code}?`)) {
      return;
    }

    this.saving = true;
    this.successMessage = null;
    this.errorMessage = null;
    try {
      const response = await this.messagingApi.deleteMailing(this.editingCampaign.code);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('mailing', 'Рассылка удалена', this.editingCampaign.code);
      await this.router.navigate(['/dashboard/mailings']);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async runDispatchQueue(): Promise<void> {
    this.dispatching = true;
    this.successMessage = null;
    this.errorMessage = null;
    this.lastDispatchResult = null;
    try {
      const response = await this.messagingApi.runMailingDispatchQueue();
      const result = this.unwrapResponse(response);
      if (!result) {
        return;
      }
      this.lastDispatchResult = result;
      this.successMessage = `Очередь обработана: отправлено ${result.sent}, ошибок ${result.failed}, перенесено ${result.rescheduled}, ожидает ${result.waiting}.`;
      this.notificationService.notify('mailing', 'Очередь рассылок обработана', `Отправлено ${result.sent.toString()}, ошибок ${result.failed.toString()}`);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.dispatching = false;
      this.changeDetector.detectChanges();
    }
  }

  public async uploadImage(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      return;
    }

    this.uploadingImage = true;
    this.errorMessage = null;
    this.successMessage = null;
    try {
      const response = await this.messagingApi.uploadMailingImage(file);
      const result = this.unwrapResponse(response);
      if (!result) {
        input.value = '';
        return;
      }

      this.formData.imageUrl = result.imageUrl;
      this.uploadedImageName = result.fileName;
      if (this.formData.imageMode === 'none') {
        this.formData.imageMode = 'caption';
      }
      input.value = '';
      this.preview = null;
      this.successMessage = 'Картинка загружена и прикреплена к рассылке.';
      this.notificationService.notify('mailing', 'Картинка рассылки загружена', result.fileName);
    } catch (error) {
      input.value = '';
      this.handleUnknownError(error);
    } finally {
      this.uploadingImage = false;
      this.changeDetector.detectChanges();
    }
  }

  public removeImage(): void {
    this.formData.imageUrl = null;
    this.uploadedImageName = null;
    if (this.formData.imageMode !== 'none') {
      this.formData.imageMode = 'none';
    }
    this.preview = null;
  }

  public getStatusLabel(status: MailingStatus): string {
    const labels: Record<MailingStatus, string> = {
      draft: 'Черновик',
      scheduled: 'Планируется',
      sending: 'В работе',
      completed: 'Выполнена',
      failed: 'Ошибка',
      cancelled: 'Отменена',
    };
    return labels[status];
  }

  public getAudienceLabel(mode: MailingAudienceMode): string {
    return this.audienceModes.find((item) => item.value === mode)?.label ?? mode;
  }

  public getCampaignRoute(campaign: MailingCampaign): string {
    return campaign.code || campaign.id;
  }

  public getSenderName(campaign: MailingCampaign): string {
    return campaign.sender?.displayName || campaign.sender?.username || 'Ответственный';
  }

  public getAccountName(campaign: MailingCampaign): string {
    return campaign.telegramAccount?.label || 'Telegram';
  }

  public getStatusClass(campaign: MailingCampaign): Record<string, boolean> {
    return {
      scheduled: campaign.status === 'scheduled',
      sending: campaign.status === 'sending',
      completed: campaign.status === 'completed',
      failed: campaign.status === 'failed',
      cancelled: campaign.status === 'cancelled',
    };
  }

  public canModifyCampaign(campaign: MailingCampaign): boolean {
    return campaign.status !== 'sending' && campaign.sentCount === 0;
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return 'Сразу';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private resolvePageMode(): void {
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    const editMatch = url.match(/\/dashboard\/mailings\/([^/]+)\/edit$/);
    if (editMatch) {
      this.mode = 'edit';
      this.mailingId = decodeURIComponent(editMatch[1]);
      return;
    }
    if (url.endsWith('/dashboard/mailings/new')) {
      this.mode = 'create';
      this.mailingId = null;
      return;
    }

    this.mode = 'list';
    this.mailingId = null;
  }

  private async loadData(showLoader = true): Promise<void> {
    this.loading = showLoader;
    this.errorMessage = null;
    try {
      const [
        contextResponse,
        campaignsResponse,
      ] = await Promise.all([
        this.messagingApi.getContext(),
        this.messagingApi.getMailings(),
      ]);
      const context = this.unwrapResponse(contextResponse);
      const campaigns = this.unwrapResponse(campaignsResponse);
      if (!context || !campaigns) {
        return;
      }

      this.context = context;
      this.campaigns = campaigns;
      this.applyDefaults();
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadEditingCampaign(): Promise<void> {
    if (!this.mailingId) {
      this.errorMessage = 'Рассылка не найдена.';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.messagingApi.getMailing(this.mailingId);
      const campaign = this.unwrapResponse(response);
      if (!campaign) {
        this.errorMessage = 'Рассылка не найдена.';
        return;
      }

      this.editingCampaign = campaign;
      this.fillFormFromCampaign(campaign);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async updateCampaign(): Promise<void> {
    if (!this.editingCampaign) {
      this.errorMessage = 'Рассылка не найдена.';
      return;
    }

    this.saving = true;
    this.successMessage = null;
    try {
      const response = await this.messagingApi.updateMailing(this.editingCampaign.code, this.preparePayload());
      const campaign = this.unwrapResponse(response);
      if (!campaign) {
        return;
      }

      this.editingCampaign = campaign;
      this.fillFormFromCampaign(campaign);
      this.preview = null;
      this.successMessage = 'Рассылка обновлена, очередь получателей пересобрана.';
      this.notificationService.notify('mailing', 'Рассылка обновлена', `${campaign.code}: ${campaign.recipientCount.toString()} получателей`);
      await this.loadData(false);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  private fillFormFromCampaign(campaign: MailingCampaign): void {
    this.formData = {
      title: campaign.title,
      senderUserId: campaign.senderUserId,
      telegramAccountId: campaign.telegramAccountId,
      audienceMode: campaign.audienceMode,
      teamIds: [...campaign.teamIds],
      messageTemplate: campaign.messageTemplate,
      scheduledAt: campaign.scheduledAt ? this.toDatetimeLocal(new Date(campaign.scheduledAt)) : this.toDatetimeLocal(new Date()),
      sendNow: campaign.scheduledAt === null,
      minDelaySeconds: campaign.minDelaySeconds,
      maxDelaySeconds: campaign.maxDelaySeconds,
      imageUrl: campaign.imageUrl,
      imageMode: campaign.imageMode,
    };
    this.uploadedImageName = campaign.imageUrl ? 'Картинка рассылки' : null;
  }

  private applyDefaults(): void {
    if (!this.formData.senderUserId) {
      this.formData.senderUserId = this.context.users[0]?.id ?? '';
    }
    if (!this.formData.telegramAccountId) {
      this.formData.telegramAccountId = this.filteredAccounts[0]?.id ?? '';
    }
  }

  private validateForm(requireRecipients: boolean): string | null {
    if (!this.formData.title.trim()) {
      return 'Укажите название рассылки.';
    }
    if (!this.formData.senderUserId) {
      return 'Выберите ответственного отправителя.';
    }
    if (!this.formData.telegramAccountId) {
      return 'Выберите Telegram-аккаунт отправителя.';
    }
    if (this.formData.audienceMode === 'team_subscribers' && this.formData.teamIds.length === 0) {
      return 'Выберите хотя бы одну команду.';
    }
    if (!this.formData.messageTemplate.trim()) {
      return 'Укажите текст сообщения.';
    }
    if (this.uploadingImage) {
      return 'Дождитесь загрузки картинки.';
    }
    if (this.formData.imageMode !== 'none' && !this.formData.imageUrl?.trim()) {
      return 'Сначала выберите файл картинки и дождитесь загрузки.';
    }
    if (!this.formData.sendNow && !this.formData.scheduledAt) {
      return 'Укажите дату отправки или выберите отправку сразу.';
    }
    if (requireRecipients && this.preview?.recipientCount === 0) {
      return 'В выбранной аудитории нет получателей.';
    }

    return null;
  }

  private preparePayload(): SaveMailingCampaignData {
    const imageUrl = this.formData.imageMode === 'none'
      ? null
      : this.formData.imageUrl?.trim() || null;

    return {
      ...this.formData,
      title: this.formData.title.trim(),
      messageTemplate: this.formData.messageTemplate.trim(),
      scheduledAt: this.formData.sendNow ? null : this.formData.scheduledAt,
      imageUrl,
      imageMode: imageUrl ? this.formData.imageMode : 'none',
    };
  }

  private createEmptyForm(): SaveMailingCampaignData {
    return {
      title: '',
      senderUserId: '',
      telegramAccountId: '',
      audienceMode: 'all_responsible_clients',
      teamIds: [],
      messageTemplate: 'Приветствую {имя клиента}, появились билеты на матч.',
      scheduledAt: this.toDatetimeLocal(new Date(Date.now() + 15 * 60 * 1000)),
      sendNow: true,
      minDelaySeconds: 5,
      maxDelaySeconds: 12,
      imageUrl: null,
      imageMode: 'none',
    };
  }

  private toDatetimeLocal(date: Date): string {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    const apiErrorMessage = error instanceof HttpErrorResponse
      ? error.error?.errorMessage ?? error.error?.message
      : null;
    this.errorMessage = apiErrorMessage ?? (error instanceof Error ? error.message : 'Не удалось выполнить действие.');
  }
}
